/**
 * Compresses an image file before encoding to base64 Data URL.
 * Ensures the output image is lightweight (~15KB - 30KB max),
 * preventing LocalStorage quota errors and Firestore document size limits.
 */
export function compressImageFile(file: File, maxWidth = 300, maxHeight = 300, quality = 0.8): Promise<string> {
  return new Promise((resolve) => {
    // If not an image, resolve empty
    if (!file || !file.type.startsWith('image/')) {
      resolve('');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const resultStr = e.target?.result as string;
      if (!resultStr) {
        resolve('');
        return;
      }

      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            resolve(dataUrl);
          } else {
            resolve(resultStr);
          }
        } catch (err) {
          console.warn('Canvas compression error, using raw string:', err);
          resolve(resultStr);
        }
      };
      img.onerror = () => resolve(resultStr);
      img.src = resultStr;
    };
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
}
